import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ActivityIcon, BellOffIcon, CheckCircle2Icon, OctagonAlertIcon, ShieldCheckIcon, ShieldAlertIcon } from 'lucide-react';
import type { AlertType } from '../../types/alert';
import { useAccount } from '../../contexts/AccountContext';
import { Panel } from '../../components/ui/Panel';
import { PanelHeader } from '../../components/ui/PanelHeader';
import { SelectInput } from '../../components/ui/SelectInput';
import { EmptyState } from '../../components/EmptyState';
import { formatDateTime, relativeTime } from '../../utils/format';
const typeMeta: Record<AlertType, {
  label: string;
  icon: typeof ShieldAlertIcon;
  tone: string;
}> = {
  expiry_warning: {
    label: 'Expiry warning',
    icon: ShieldAlertIcon,
    tone: 'bg-warn-soft text-warn-ink border-warn-border'
  },
  certificate_renewed: {
    label: 'Certificate renewed',
    icon: ShieldCheckIcon,
    tone: 'bg-healthy-soft text-healthy-ink border-healthy-border'
  },
  site_down: {
    label: 'Site down',
    icon: OctagonAlertIcon,
    tone: 'bg-critical-soft text-critical-ink border-critical-border'
  },
  site_recovered: {
    label: 'Site recovered',
    icon: ActivityIcon,
    tone: 'bg-healthy-soft text-healthy-ink border-healthy-border'
  }
};
const deliveryMeta = {
  delivered: {
    label: 'Delivered',
    className: 'text-healthy-ink'
  },
  pending: {
    label: 'Queued',
    className: 'text-warn-ink'
  },
  failed: {
    label: 'Partly failed',
    className: 'text-critical-ink'
  }
} as const;
export function Alerts() {
  const {
    alerts
  } = useAccount();
  const [type, setType] = useState<'all' | AlertType>('all');
  const [domain, setDomain] = useState('all');
  const domains = useMemo(() => Array.from(new Set(alerts.map(alert => alert.domain))).sort(), [alerts]);
  const visible = alerts.filter(alert => (type === 'all' || alert.type === type) && (domain === 'all' || alert.domain === domain));
  return <div className="space-y-5" data-id="element-764" data-mp="2vcjssdcuymco">
      <div data-id="element-765" data-mp="2vdlercvewt96">
        <h1 className="text-xl font-semibold tracking-tight text-ink" data-id="element-766" data-mp="2vdtpay42bmh7">
          Alert history
        </h1>
        <p className="mt-1 max-w-2xl text-[13px] text-ink-muted" data-id="element-767" data-mp="30e913l8p4lx2">
          Every notification we have sent on your behalf, with who received it
          and whether it landed.
        </p>
      </div>

      <div className="flex flex-wrap gap-2" data-id="element-768" data-mp="2qk8t9bm152kn">
        <div className="w-[200px]" data-id="element-769" data-mp="2cmanuvposgha">
          <label htmlFor="alert-type" className="sr-only" data-id="element-770" data-mp="2cl90brrhi1io">
            Filter by type
          </label>
          <SelectInput id="alert-type" value={type} onChange={event => setType(event.target.value as 'all' | AlertType)} options={[{
          value: 'all',
          label: 'All alert types'
        }, {
          value: 'expiry_warning',
          label: 'Expiry warnings'
        }, {
          value: 'certificate_renewed',
          label: 'Certificates renewed'
        }, {
          value: 'site_down',
          label: 'Sites down'
        }, {
          value: 'site_recovered',
          label: 'Sites recovered'
        }]} data-id="element-771" data-mp="2hmybr8imch7a" />
        </div>
        <div className="w-[200px]" data-id="element-772" data-mp="1me4k6yls9pa0">
          <label htmlFor="alert-domain" className="sr-only" data-id="element-773" data-mp="203y9fi17abk9">
            Filter by domain
          </label>
          <SelectInput id="alert-domain" value={domain} onChange={event => setDomain(event.target.value)} options={[{
          value: 'all',
          label: 'All domains'
        }, ...domains.map(item => ({
          value: item,
          label: item
        }))]} data-id="element-774" data-mp="1vd823z97rymx" />
        </div>
      </div>

      <Panel as="section" className="overflow-hidden" data-id="element-775" data-mp="2j1j1exz9hq93">
        <PanelHeader title={`${visible.length} ${visible.length === 1 ? 'notification' : 'notifications'}`} description="Newest first. Retained for the length of your plan's history window." data-id="element-776" data-mp="2e3zaoyldnl09" />

        {visible.length === 0 ? <EmptyState icon={BellOffIcon} title="Nothing sent yet" body="When a certificate enters its warning window or a site stops responding, the email we send will be recorded here." data-id="element-777" data-mp="js2rfsx39lga" /> : <ul className="divide-y divide-line" data-id="element-778" data-mp="v6331l9k38al">
            {visible.map(alert => {
          const meta = typeMeta[alert.type];
          const delivery = deliveryMeta[alert.delivery];
          return <li key={alert.id} className="px-5 py-4" data-id="element-779" data-mp="2hyqcfskhuye">
                  <div className="flex flex-wrap items-start gap-3" data-id="element-780" data-mp="7exiwtwl1tuh">
                    <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border ${meta.tone}`} data-id="element-781" data-mp="7d4gxrwwzxst">
                      <meta.icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-782" data-mp="h6l96rhel4ud" />
                    </span>

                    <div className="min-w-0 flex-1" data-id="element-783" data-mp="3ft68dt3t64if">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1" data-id="element-784" data-mp="3ktlimbssmvw6">
                        <span className="text-[13px] font-semibold text-ink" data-id="element-785" data-mp="3dbdwdlekva57">
                          {meta.label}
                        </span>
                        <Link to={`/app/monitors/${alert.monitorId}`} className="text-[13px] text-brand hover:text-brand-hover" data-id="element-786" data-mp="38cj3trqt677w">
                          {alert.domain}
                        </Link>
                      </div>
                      <p className="mt-1 text-[13px] leading-relaxed text-ink-muted" data-id="element-787" data-mp="2udb673aan75t">
                        {alert.detail}
                      </p>
                      <p className="mt-1.5 truncate text-xs text-ink-soft" data-id="element-788" data-mp="2kldqbj8cbr0r">
                        To {alert.recipients.join(', ')}
                      </p>
                      {alert.failureReason && <p className="mt-1.5 flex items-start gap-1.5 text-xs text-critical-ink" data-id="element-789" data-mp="2amxx9apr67om">
                          <OctagonAlertIcon className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" data-id="element-790" data-mp="26ngreg6ez8ix" />
                          Not delivered to everyone — {alert.failureReason}
                        </p>}
                    </div>

                    <div className="shrink-0 text-right" data-id="element-791" data-mp="1n2gig4ps4muu">
                      <p className="tabular text-xs text-ink-muted" data-id="element-792" data-mp="1ryx9p7s9zhc5">
                        {formatDateTime(alert.sentAt)}
                      </p>
                      <p className="mt-0.5 text-xs text-ink-soft" data-id="element-793" data-mp="2frdys2afk9ky">
                        {relativeTime(alert.sentAt)}
                      </p>
                      <p className={`mt-1.5 flex items-center justify-end gap-1 text-[11px] font-medium ${delivery.className}`} data-id="element-794" data-mp="2609ld70ekws8">
                        {alert.delivery === 'delivered' && <CheckCircle2Icon className="h-3 w-3" aria-hidden="true" data-id="element-795" data-mp="2ymueod0fviyf" />}
                        {delivery.label}
                      </p>
                    </div>
                  </div>
                </li>;
        })}
          </ul>}
      </Panel>
    </div>;
}